import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { EXPERIENCES } from "../data/experiences";
import { Reveal, Ornament, SectionHeader } from "./Ornament";
import Seo from "./Seo";
import Services from "./Services";
import ConceptsFan from "./ConceptsFan";
import { ArrowUpRight } from "lucide-react";

const BASE = "https://lumiereevents.org";

const EXPERIENCES_JSONLD = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: "Signature Luxury Event Experiences — Lumière Entertainment",
  numberOfItems: EXPERIENCES.length,
  itemListElement: EXPERIENCES.map((e, i) => ({
    "@type": "ListItem",
    position: i + 1,
    item: {
      "@type": "Service",
      name: e.name,
      description: e.desc,
      category: e.subtitle,
      image: e.image && e.image.startsWith("http") ? e.image : BASE + (e.image || "/generated/hero.jpg"),
      provider: { "@id": BASE + "/#org" },
      areaServed: ["Greece", "Italy", "France", "Spain", "UAE", "Worldwide"],
    },
  })),
};

const useTop = () => {
  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, []);
};

/* ------------ Single Experience Card ------------ */
export function ExperienceCard({ item, index }) {
  const nav = useNavigate();
  const reverse = index % 2 === 1;
  return (
    <Reveal
      className="border-t border-[var(--lu-gold-dark)]/30 py-16 lg:py-24"
      data-testid={`experience-${item.slug}`}
    >
      <div
        className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start ${
          reverse ? "lg:[direction:rtl]" : ""
        }`}
      >
        {/* Visual */}
        <div className="lg:col-span-6 lg:[direction:ltr]">
          <div
            className={`tile ${
              item.image && item.image.includes("mockup-")
                ? "aspect-[3/2] bg-[var(--lu-cream)] flex items-center justify-center p-2"
                : "aspect-[4/5]"
            } overflow-hidden`}
          >
            {item.isVideo ? (
              <video
                src={item.image}
                autoPlay
                muted
                loop
                playsInline
                className="w-full h-full object-cover"
              />
            ) : (
              <img
                src={item.image}
                alt={item.name}
                className={`w-full h-full ${
                  item.image && item.image.includes("mockup-")
                    ? "object-contain"
                    : "object-cover"
                }`}
                loading="lazy"
              />
            )}
          </div>
        </div>

        {/* Copy */}
        <div className="lg:col-span-6 lg:[direction:ltr]">
          <p className="font-micro text-[11px] text-[var(--lu-gold-dark)] tracking-[0.3em]">
            {item.num}. {item.subtitle}
          </p>
          <h3 className="mt-5 h-editorial text-[38px] sm:text-[48px] lg:text-[62px] text-[var(--lu-black)] leading-[0.98]">
            {item.name}
          </h3>
          <div className="deco-line-solid my-8 max-w-[180px] bg-[var(--lu-gold-dark)]/60" />
          <p className="text-[15px] leading-[1.9] text-black-75 font-light max-w-xl">
            {item.desc}
          </p>

          {/* Structured sections */}
          {item.highlights && (
            <div className="mt-10">
              <p className="kicker text-[var(--lu-gold-dark)]">Experience Highlights</p>
              <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-[14px] text-black-80 font-light">
                {item.highlights.map((it, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="mt-2 inline-block h-1 w-1 rotate-45 bg-[var(--lu-gold-dark)]" />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {item.includes && (
            <div className="mt-10">
              <p className="kicker text-[var(--lu-gold-dark)]">Includes</p>
              <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-[14px] text-black-80 font-light">
                {item.includes.map((it, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="mt-2 inline-block h-1 w-1 rotate-45 bg-[var(--lu-gold-dark)]" />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {item.entertainment && (
            <div className="mt-8">
              <p className="kicker text-[var(--lu-gold-dark)]">Entertainment</p>
              <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-[14px] text-black-80 font-light">
                {item.entertainment.map((it, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="mt-2 inline-block h-1 w-1 rotate-45 bg-[var(--lu-gold-dark)]" />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {item.arrival && (
            <div className="mt-8">
              <p className="kicker text-[var(--lu-gold-dark)]">Arrival Experience</p>
              <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-[14px] text-black-80 font-light">
                {item.arrival.map((it, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="mt-2 inline-block h-1 w-1 rotate-45 bg-[var(--lu-gold-dark)]" />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {item.installations && (
            <div className="mt-8">
              <p className="kicker text-[var(--lu-gold-dark)]">Editorial Installations</p>
              <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-[14px] text-black-80 font-light">
                {item.installations.map((it, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="mt-2 inline-block h-1 w-1 rotate-45 bg-[var(--lu-gold-dark)]" />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {item.bar && (
            <div className="mt-8">
              <p className="kicker text-[var(--lu-gold-dark)]">Luxury Bar Experience</p>
              <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-[14px] text-black-80 font-light">
                {item.bar.map((it, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="mt-2 inline-block h-1 w-1 rotate-45 bg-[var(--lu-gold-dark)]" />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {item.styling && (
            <div className="mt-8 border-l-2 border-[var(--lu-gold-dark)]/50 pl-5 space-y-2 text-[13px] font-light text-black-70">
              <p><span className="font-micro text-[10px] text-[var(--lu-gold-dark)] mr-2 uppercase">Palette</span>{item.styling.palette}</p>
              <p><span className="font-micro text-[10px] text-[var(--lu-gold-dark)] mr-2 uppercase">Flowers</span>{item.styling.flowers}</p>
              <p><span className="font-micro text-[10px] text-[var(--lu-gold-dark)] mr-2 uppercase">Materials</span>{item.styling.materials}</p>
            </div>
          )}

          {item.decor && (
            <div className="mt-8">
              <p className="kicker text-[var(--lu-gold-dark)]">Ocean-Inspired Decor & Ambience</p>
              <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-[14px] text-black-80 font-light">
                {item.decor.map((it, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="mt-2 inline-block h-1 w-1 rotate-45 bg-[var(--lu-gold-dark)]" />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {item.cocktails && (
            <div className="mt-8">
              <p className="kicker text-[var(--lu-gold-dark)]">Signature Cocktails & Cuisine</p>
              <ul className="mt-4 space-y-2 text-[14px] text-black-80 font-light">
                {item.cocktails.map((it, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="mt-2 inline-block h-1 w-1 rotate-45 bg-[var(--lu-gold-dark)]" />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {item.atmosphere && (
            <div className="mt-8">
              <p className="kicker text-[var(--lu-gold-dark)]">Atmosphere</p>
              <ul className="mt-4 space-y-2 text-[14px] text-black-80 font-light">
                {item.atmosphere.map((it, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="mt-2 inline-block h-1 w-1 rotate-45 bg-[var(--lu-gold-dark)]" />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {item.modules && (
            <div className="mt-10">
              <p className="kicker text-[var(--lu-gold-dark)]">
                {item.modules.length} Signature Modules
              </p>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {item.modules.map((m, i) => (
                  <div
                    key={i}
                    className="p-4 border border-[var(--lu-gold-dark)]/25 bg-[var(--lu-cream)]/50"
                  >
                    <p className="font-micro text-[10px] text-[var(--lu-gold-dark)]">
                      {m.n}
                    </p>
                    <p className="mt-2 font-serif text-[17px] text-[var(--lu-black)] leading-tight">
                      {m.title}
                    </p>
                    <p className="mt-2 text-[12.5px] leading-[1.6] text-black-70 font-light">
                      {m.body}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Perfect for */}
          <div className="mt-10 p-5 border border-[var(--lu-gold-dark)]/30 bg-[var(--lu-cream)]/60">
            <p className="font-micro text-[10px] text-[var(--lu-gold-dark)] mb-2">Perfect For</p>
            <p className="text-[13px] font-light text-black-80 leading-relaxed">
              {item.perfect}
            </p>
          </div>

          {/* Note (e.g. Naya photos coming soon) */}
          {item.note && (
            <p className="mt-4 font-serif italic text-[12px] text-[var(--lu-gold-dark)]">
              {item.note}
            </p>
          )}

          {/* CTA */}
          <button
            onClick={() => nav("/contact")}
            className="mt-10 inline-flex items-center gap-2 font-micro text-[11px] text-[var(--lu-gold-dark)] hover:text-[var(--lu-gold)] group"
            data-testid={`experience-cta-${item.slug}`}
          >ASK FOR THIS EXPERIENCE<ArrowUpRight
              size={14}
              className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"
            />
          </button>
        </div>
      </div>

      {/* Chapters — full-width narrative story below the card (Sea Nymphs) */}
      {item.chapters && (
        <div className="mt-16 space-y-16">
          {item.chapters.map((ch, i) => (
            <div
              key={i}
              className={`grid grid-cols-1 lg:grid-cols-12 gap-10 items-center ${
                i % 2 === 0 ? "" : "lg:[direction:rtl]"
              }`}
            >
              <div className="lg:col-span-5 lg:[direction:ltr]">
                <div className="tile aspect-[4/5]">
                  <img
                    src={ch.image}
                    alt={ch.title}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
              <div className="lg:col-span-7 lg:[direction:ltr]">
                <p className="kicker text-[var(--lu-gold-dark)]">
                  Chapter {String(i + 1).padStart(2, "0")}
                </p>
                <h4 className="mt-4 font-serif text-[28px] sm:text-[34px] text-[var(--lu-black)] leading-tight">
                  {ch.title}
                </h4>
                <p className="mt-6 text-[15px] leading-[1.9] text-black-75 font-light">
                  {ch.text}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Epilogue */}
      {item.epilogue && (
        <div className="mt-16 max-w-3xl mx-auto text-center">
          <Ornament className="mb-8" />
          <p className="font-serif italic text-[22px] sm:text-[28px] leading-[1.4] text-[var(--lu-gold-dark)]">
            {item.epilogue}
          </p>
        </div>
      )}
    </Reveal>
  );
}

/* ------------ Experiences Page ------------ */
export default function ExperiencesPage() {
  useTop();
  return (
    <div className="pt-32 bg-[var(--lu-ivory)]" data-testid="experiences-page">
      <Seo
        path="/experiences"
        title="Signature Experiences — 20 Luxury Event Concepts | Lumière Entertainment"
        description="20 signature luxury event concepts: Fly Me to the Moon (wedding), White Party, Lake Doxa autumn, Wild Night fashion, Disco Couture, Innovation tech activation, Bollywood Celebration, Sea Nymphs yacht, Sky Performance brand reveal, Editorial Beauty, Creative Luxury Bars, Signature Installations, Sicilian Dolce Vita, Bachelorette Experience, The Last Sunset Before I Do, Wellness & Sound, Mediterranean Grazing, Minoan Crete, Roman Empire, Charity Festivals. Bespoke production in Greece, Italy, France, Spain, UAE and worldwide."
        keywords="luxury event concepts, wedding entertainment concepts, White Party concept, Bollywood wedding, Sicilian Dolce Vita, Bachelorette Experience luxury, destination wedding concepts, aerial brand activation, Minoan Crete wedding, Roman Empire wedding, luxury yacht entertainment, Sea Nymphs concept"
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Experiences", path: "/experiences" },
        ]}
        jsonLd={EXPERIENCES_JSONLD}
      />

      {/* Header */}
      <section className="container-lux text-center pb-4">
        <p className="kicker text-[var(--lu-gold-dark)]">Signature Concepts</p>
        <h1 className="mt-6 h-editorial text-[52px] sm:text-[72px] lg:text-[108px] text-[var(--lu-black)] leading-[0.98]">
          Experiences
        </h1>
        <Ornament className="my-10" />
        <p className="text-[19px] font-serif italic text-[var(--lu-gold-dark)] max-w-2xl mx-auto">
          Where imagination becomes reality.
        </p>
        <p className="mt-6 text-[15px] leading-[1.9] text-black-70 font-light max-w-3xl mx-auto">
          Every concept is designed exclusively for the client, combining
          storytelling, entertainment, scenography and unforgettable moments.
        </p>
      </section>

      {/* Concepts Fan — a curated visual overview */}
      <ConceptsFan />

      {/* All experiences (excluding items shown on Concept Store page) */}
      <section className="section-y">
        <div className="container-lux">
          {EXPERIENCES.filter(
            (e) =>
              !["creative-luxury-bars", "signature-installations"].includes(
                e.slug
              )
          ).map((item, i) => (
            <ExperienceCard key={item.slug} item={item} index={i} />
          ))}
        </div>
      </section>

      {/* Signature Acts — moved from Services page */}
      <Services />

      {/* Final CTA */}
      <section className="section-y bg-[var(--lu-black)] grain">
        <div className="container-lux text-center">
          <SectionHeader
            kicker="Let's design your own"
            title="One of them — or"
            italicTitle="something completely new."
            light
          />
          <p className="mt-8 text-[15px] leading-[1.9] text-ivory-75 font-light max-w-2xl mx-auto">Each concept is completely bespoke to your own vision, space and
            public. Choose an experience and customize it, or let us
            create something completely original.</p>
          <div className="mt-12 flex flex-col sm:flex-row gap-5 justify-center">
            <a
              href="/contact"
              className="btn-gold"
              data-testid="experiences-cta-proposal"
            >Ask for Bespoke Concept</a>
            <a
              href="/services"
              className="btn-gold-outline"
              data-testid="experiences-cta-services"
            >See Services</a>
          </div>
        </div>
      </section>
    </div>
  );
}
